import React, { useState } from 'react';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import { Container, Paper, Typography, TextField, Button, Alert, Box, Link } from '@mui/material';
import apiService from '../../services/api';

/**
 * Choose a new password from the emailed link (security hardening batch 3, 30 Sep 2026).
 * The link works once, for 30 minutes. A successful reset signs out every session and
 * lifts any sign-in lock.
 */
export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);

  const mismatch = confirm && password !== confirm;

  const submit = async (e) => {
    e.preventDefault();
    if (!password || mismatch) return;
    setBusy(true);
    setError('');
    try {
      const res = await apiService.resetPassword(token, password);
      setDone(res.msg || 'Your password has been changed. You can now sign in.');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Paper elevation={3} sx={{ mt: 8, p: 4 }}>
        <Typography component="h1" variant="h5" gutterBottom>Choose a new password</Typography>
        {!token && <Alert severity="error">This reset link is not complete. Please use the link from the email, or ask for a new one from the sign-in page.</Alert>}
        {done ? (
          <>
            <Alert severity="success" sx={{ mb: 2 }}>{done}</Alert>
            <Link component={RouterLink} to="/login">Go to sign in</Link>
          </>
        ) : token && (
          <Box component="form" onSubmit={submit} noValidate>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <TextField
              id="reset-password-new"
              margin="normal" fullWidth type="password" label="New password" autoComplete="new-password"
              value={password} onChange={e => setPassword(e.target.value)}
              helperText="At least 8 characters, with an uppercase letter, a lowercase letter, a number and a symbol"
            />
            <TextField
              id="reset-password-confirm"
              margin="normal" fullWidth type="password" label="Confirm new password" autoComplete="new-password"
              value={confirm} onChange={e => setConfirm(e.target.value)}
              error={!!mismatch} helperText={mismatch ? 'The passwords do not match' : ' '}
            />
            <Button type="submit" fullWidth variant="contained" sx={{ mt: 2 }} disabled={busy || !password || !!mismatch}>
              {busy ? 'Saving…' : 'Change password'}
            </Button>
            <Box sx={{ mt: 2 }}>
              <Link component={RouterLink} to="/login">Back to sign in</Link>
            </Box>
          </Box>
        )}
      </Paper>
    </Container>
  );
}
